/**
 * Models the workflow of a Diagnosis Review Group Adds edit form
 */

import Radio from "backbone.radio";
import BaseModel from "./drg-adds-edit";
import Tooltip from "../../models/tooltip";
import ClaimsList from "../../collections/chart-reviews/claims";
import DiagnosisAttributesCollection from "../../collections/chart-reviews/diag-attributes";
import StringKeyValueCollection from "../../collections/key-string-value";
import NumericKeyValueCollection from "../../collections/key-numeric-value";
import NoteCollection from "../../collections/notes";
import UserCollection from "../../collections/chart-reviews/reviewers/selectable";
import MemberCategories from "../../models/chart-reviews/member-category";

const channel = Radio.channel("global");

const MATCHPOINT_BUTTON_LABEL_CHOOSE = "Choose...";
const MATCHPOINT_BUTTON_LABEL_VIEW = "View...";

const Model = BaseModel.extend({
  namedAttributes: {
    // indicates whether the DRG can be edited
    _canEdit: Boolean,
    _canVoid: Boolean,

    canChooseMatchPointClaim: Boolean,
    matchPointClaimButtonEnabled: Boolean,
    matchPointClaimButtonLabel: String,
    userIsChartReviewAdmin: Boolean,

    // last saved
    lastSavedDisposition: String,

    // strongly typed props for proper display in UI
    statusReasons: Tooltip,
    users: UserCollection,

    dispositionLabel: String,
    dispositions: StringKeyValueCollection,
    auxTypes: StringKeyValueCollection,
    placesOfService: StringKeyValueCollection,
    supplementalReasonCodes: StringKeyValueCollection,
    diagnosisAttributesCollection: DiagnosisAttributesCollection,
    diagnosisAttributesList: NumericKeyValueCollection,
    diagnosisAttributesLabels: Tooltip,
    claimsList: ClaimsList,
    claimSelections: StringKeyValueCollection,
    evaluationActions: StringKeyValueCollection,
    notes: NoteCollection,
    notesList: NoteCollection,
    statusList: StringKeyValueCollection,
    statusLabel: String,
    statusMessage: String,
    memberCategories: MemberCategories,

    canEditDisposition: Boolean,
    canEditDiagnosisAttributes: Boolean,
    canEditClientClaimNbr: Boolean,
    canEditClientCommercialPlanId: Boolean,
    canEditMatchPointClaimNbr: Boolean,
    canEditActionApprove: Boolean,
    claimSelectionEditRules: Object,
    evaluationActionEditRules: Object,
  },

  initialize() {
    // do workflow whenever the model changes
    this.listenTo(this, "change", this._doWorkflow);
    this.listenTo(
      this,
      "change:claimSelections",
      this._onClaimSelectionsChange
    );
    this.listenTo(
      this,
      "change:evaluationActions",
      this._onEvaluationActionsChange
    );

    // set initial values
    this.set({
      // assume DRG is editable until API says otherwise
      _canEdit: true,
      _canVoid: false,

      // BugzId 85726
      userIsChartReviewAdmin: channel.request("user:has-any-role", [
        "ROLE_CHART_REVIEW_ADMIN",
      ]),

      statusLabel: null,
      canEditDisposition: false,
      canEditDiagnosisAttributes: false,
      canEditClientClaimNbr: false,
      canEditMatchPointClaimNbr: false,
      canEditActionApprove: false,
      claimSelectionEditRules: {},
      evaluationActionEditRules: {},
      memberCategories: new MemberCategories(),
    });
  },

  _doWorkflow() {
    // localize props
    const currentStatus = this.get("statusValue");
    const currentDisposition = this.get("disposition");
    const lastSavedDisposition = this.get("lastSavedDisposition");
    const currentClaimSelection = this.get("claimSelection");
    const currentEvaluationAction = this.get("evaluationAction");
    const matchesFound = this.get("matchesFound");

    // set status
    let statusLabel = null;
    const statusList = this.get("statusList");
    if (statusList && currentStatus) {
      const label = statusList.get(currentStatus);
      if (label) {
        statusLabel = label.get("value");
      }
    }

    // define rules
    const userHasValidRole = channel.request("user:has-any-role", [
      "ROLE_ADMIN",
      "ROLE_CHART_REVIEW_ADMIN",
      "ROLE_CHART_REVIEWER",
    ]);
    const userHasValidAdminRole = channel.request("user:has-any-role", [
      "ROLE_ADMIN",
      "ROLE_CHART_REVIEW_ADMIN",
    ]);

    const dispositionRule =
      this.get("_canVoid") ||
      _.contains(
        ["NOT_READY", "UNDER_EVALUATION", "REJECTED", "ACCEPTED"],
        currentStatus
      );

    const diagnosisAttributesRule = _.contains(
      ["NOT_READY", "UNDER_EVALUATION", "REJECTED"],
      currentStatus
    );

    const clientClaimNbrRule1 = _.contains(
      ["NOT_READY", "UNDER_EVALUATION", "REJECTED"],
      currentStatus
    );
    const clientClaimNbrRule2 =
      "V" === lastSavedDisposition && "ACCEPTED" === currentStatus;
    const clientClaimNbrRule3 = _.contains(["A"], currentDisposition);
    const canEditClientClaimNbrRule =
      clientClaimNbrRule1 || (clientClaimNbrRule2 && clientClaimNbrRule3);

    // added canEditClientClaimNbrRule per BugzId 85309
    const clientCommercialPlanIdRule =
      !_.isEmpty(this.get("clientClaimNbr")) && canEditClientClaimNbrRule;

    const matchPointClaimNbrRule = "A" === currentDisposition;

    const claimSelectionClientRule1 = _.contains(["A"], currentDisposition);
    const claimSelectionClientRule2 = _.contains(
      ["NOT_READY", "REJECTED"],
      currentStatus
    );
    const claimSelectionClientRule3 = "UNDER_EVALUATION" === currentStatus;
    const claimSelectionClientRule4 =
      "ACCEPTED" === currentStatus && "V" === lastSavedDisposition;
    const claimSelectionClientRule5 =
      clientCommercialPlanIdRule &&
      !_.isEmpty(this.get("clientCommercialPlanId"));
    const canEditClaimSelectionClient =
      claimSelectionClientRule1 &&
      ((userHasValidRole && claimSelectionClientRule2) ||
        claimSelectionClientRule3 ||
        claimSelectionClientRule4) &&
      claimSelectionClientRule5;

    const claimSelectionMatchpointRule1 = "A" === currentDisposition;
    const claimSelectionMatchpointRule2A =
      _.contains(["NOT_READY", "REJECTED"], currentStatus) && userHasValidRole;
    const claimSelectionMatchpointRule2B = "UNDER_EVALUATION" === currentStatus;
    const claimSelectionMatchpointRule3 =
      !_.isEmpty(this.get("matchPointClaimNbr")) &&
      !_.isEmpty(this.get("matchPointCommercialPlanId"));
    const canEditClaimSelectionMatchpoint =
      claimSelectionMatchpointRule1 &&
      (claimSelectionMatchpointRule2A || claimSelectionMatchpointRule2B) &&
      claimSelectionMatchpointRule3;

    const canEditEvaluationActionStartRule1 = _.contains(
      ["NOT_READY", "REJECTED"],
      currentStatus
    );
    const canEditEvaluationActionStartRule2A = "ACCEPTED" === currentStatus;
    const canEditEvaluationActionStartRule2B =
      "V" === lastSavedDisposition && _.contains(["A"], currentDisposition);
    const canEditEvaluationActionStartRule2C =
      "V" === currentDisposition && _.contains(["A"], lastSavedDisposition);
    const canEditEvaluationActionStartRule2 =
      (canEditEvaluationActionStartRule2A &&
        canEditEvaluationActionStartRule2B) ||
      canEditEvaluationActionStartRule2C;
    const canEditEvaluationActionStart =
      canEditEvaluationActionStartRule1 || canEditEvaluationActionStartRule2;

    const canEditEvaluationActionContinue =
      "UNDER_EVALUATION" === currentStatus;
    const canEditEvaluationActionMarkCompleted =
      canEditEvaluationActionContinue &&
      (!_.isEmpty(currentClaimSelection) ||
        !_.isEmpty(this.get("auditClaimNbr")));
    const canEditEvaluationActionMarkRematch = canEditEvaluationActionContinue;
    const canEditEvaluationActionMarkInvalid = _.contains(
      ["NOT_READY", "REJECTED", "UNDER_EVALUATION"],
      currentStatus
    );
    const canEditEvaluationActionMarkValid =
      this._hasStatusReason("UE004") &&
      this.get("userIsChartReviewAdmin") &&
      "UNDER_EVALUATION" === currentStatus;
    const canEditEvaluationActionApproveCompletedRule1 =
      "UNDER_EVALUATION" === currentStatus;
    const canEditEvaluationActionApproveCompletedRule2 =
      this._hasStatusReason("UE002");
    const canEditEvaluationActionApproveCompletedRule3 = userHasValidAdminRole;
    const canEditEvaluationActionApproveCompletedRule4 =
      "A" === currentDisposition && !this._hasRejectedDiagnosisAttributes();
    const canEditEvaluationActionApproveCompletedRule5 = _.contains(
      ["D", "V"],
      currentDisposition
    );
    const canEditEvaluationActionApproveCompleted =
      canEditEvaluationActionApproveCompletedRule1 &&
      canEditEvaluationActionApproveCompletedRule2 &&
      canEditEvaluationActionApproveCompletedRule3 &&
      (canEditEvaluationActionApproveCompletedRule4 ||
        canEditEvaluationActionApproveCompletedRule5);
    const canEditEvaluationActionApproveRematch =
      canEditEvaluationActionContinue &&
      userHasValidAdminRole &&
      this._hasStatusReason("UE003");

    const canEditActionApproveRule1 = _.contains(
      ["NOT_READY", "REJECTED"],
      currentStatus
    );
    const canEditActionApproveRule2A = "ACCEPTED" === currentStatus;
    const canEditActionApproveRule2B =
      "V" === lastSavedDisposition && _.contains(["A"], currentDisposition);
    const canEditActionApproveRule2C =
      "V" === currentDisposition && _.contains(["A"], lastSavedDisposition);
    const canEditActionApproveRule2 =
      canEditActionApproveRule2A &&
      (canEditActionApproveRule2B || canEditActionApproveRule2C);
    const canEditActionApproveRule3 =
      !_.isEmpty(currentClaimSelection) &&
      userHasValidAdminRole &&
      _.isEmpty(currentEvaluationAction);
    const canEditActionApproveRule4A =
      canEditEvaluationActionApproveCompletedRule4;
    const canEditActionApproveRule4B =
      canEditEvaluationActionApproveCompletedRule5;
    const canEditActionApproveRule4 =
      canEditActionApproveRule4A || canEditActionApproveRule4B;
    const canEditActionApprove =
      (canEditActionApproveRule1 || canEditActionApproveRule2) &&
      canEditActionApproveRule3 &&
      canEditActionApproveRule4;

    // Begin bugzId 81569
    const canEdit = this.get("canEdit");
    const canChooseMatchPointClaim = !_.contains(
      ["READY", "SENT", "ACCEPTED"],
      currentStatus
    );
    let matchPointClaimButtonEnabled = false;
    let matchPointClaimButtonLabel = MATCHPOINT_BUTTON_LABEL_VIEW;

    // enable button only when cmRunSid is not null
    if (this.get("cmRunSid") && matchesFound) {
      matchPointClaimButtonEnabled = true;
    }

    // set label to "choose" when appropriate
    if (canEdit && canChooseMatchPointClaim) {
      matchPointClaimButtonLabel = MATCHPOINT_BUTTON_LABEL_CHOOSE;
    }
    // End bugzId 81569

    // apply rules to display model
    this.set({
      _canEdit: this.get("canEdit"),
      _canVoid: this.get("canVoid"),
      canChooseMatchPointClaim,
      matchPointClaimButtonEnabled,
      matchPointClaimButtonLabel,
      statusLabel,
      canEditDisposition: dispositionRule,
      canEditDiagnosisAttributes: diagnosisAttributesRule,
      canEditClientClaimNbr: canEditClientClaimNbrRule,
      canEditClientCommercialPlanId: clientCommercialPlanIdRule,
      canEditMatchPointClaimNbr: matchesFound && matchPointClaimNbrRule,
      canEditActionApprove,
      claimSelectionEditRules: {
        CLIENT: canEditClaimSelectionClient,
        MATCHPOINT: canEditClaimSelectionMatchpoint,
      },
      evaluationActionEditRules: {
        APPROVE_SUBMISSION: canEditActionApprove,
        START: canEditEvaluationActionStart,
        CONTINUE: canEditEvaluationActionContinue,
        MARK_COMPLETED: canEditEvaluationActionMarkCompleted,
        MARK_REMATCH: canEditEvaluationActionMarkRematch,
        MARK_INVALID: canEditEvaluationActionMarkInvalid,
        MARK_VALID: canEditEvaluationActionMarkValid,
        APPROVE_COMPLETE: canEditEvaluationActionApproveCompleted,
        APPROVE_REMATCH: canEditEvaluationActionApproveRematch,
      },
    });

    this._processDiagnosisAttributes();
    this._onClaimSelectionsChange();
    this._onEvaluationActionsChange();

    // run default validation
    this.isValid();

    // set the statue message to the current validation error
    this.set("statusMessage", this.validationError);
  },

  _hasRejectedDiagnosisAttributes() {
    let rejectedCount = 0;

    const coll = this.get("diagnosisAttributesCollection");
    const selected = this.get("diagnosisAttributes");

    if (coll) {
      rejectedCount = selected
        .map((id) => coll.get(id))
        .filter((model) => model && model.isRejected()).length;
    }

    return !!rejectedCount;
  },

  _hasStatusReason(code) {
    const statusReasons = this.get("statusReasons");
    if (statusReasons) {
      const tooltip = statusReasons.get("tooltip");
      if (tooltip) {
        const match = !!tooltip.get(code);
        return match;
      }
    }
    return false;
  },

  _processDiagnosisAttributes() {
    // grab attributes
    const attrs = this.get("diagnosisAttributes");

    // grab attributes list
    const data = this.get("diagnosisAttributesList");
    if (data) {
      // enable selected diag attributes
      data.map((attr) => {
        const checked = _.contains(attrs, attr.getKey()) ? true : false;
        attr.setChecked(checked);
      });

      // save updated selections to model
      this.set("diagnosisAttributesList", data);
    }
  },

  // applies workflow generated rules to claim selections
  _onClaimSelectionsChange() {
    // grab rules
    const rules = this.get("claimSelectionEditRules");

    // grab selections from model
    const data = this.get("claimSelections");
    if (data) {
      // iterate over selections
      data.map((item) => {
        const enabled = rules[item.getKey()] || false;
        item.setEnabled(enabled);
      });

      // save updated selections to model
      this.set("claimSelections", data);
    }
  },

  // applies workflow generated rules to evaluation actions
  _onEvaluationActionsChange() {
    // grab rules
    const rules = this.get("evaluationActionEditRules");

    // grab selections from model
    const data = this.get("evaluationActions");
    if (data) {
      // iterate over selections
      data.map((item) => {
        const enabled = rules[item.getKey()] || false;
        item.setEnabled(enabled);
      });

      // save updated selections to model
      this.set("evaluationActions", data);
    }
  },
});

export { Model as default };
